import { z } from 'zod'
import {
  PropertyType,
  PropertyUse,
  StateAbbreviation,
} from './generated/graphql-types'

export enum HelocPartner {
  Figure = 'figure',
  NftyDoor = 'nftydoor',
  SpringEq = 'springeq',
}

export const helocPartnerSchema = z.nativeEnum(HelocPartner)

const PARTNER_FILE_NAME_PREFIXES: ReadonlyArray<[RegExp, HelocPartner]> = [
  [/^figure_/, HelocPartner.Figure],
  [/^nftydoor/, HelocPartner.NftyDoor],
  [/^spring eq/, HelocPartner.SpringEq],
]

/**
 * Maps a vectorized guideline file name to its partner until a partner column exists on
 * `loan_assistant_document_vectors`. Case-insensitive; collapses repeated whitespace because
 * the real names are inconsistently spaced.
 */
export function partnerFromFileName(fileName: string): HelocPartner | null {
  const normalized = fileName.trim().replace(/\s+/g, ' ').toLowerCase()
  const match = PARTNER_FILE_NAME_PREFIXES.find(([pattern]) =>
    pattern.test(normalized)
  )
  return match ? match[1] : null
}

// Process documents (app stages, status tables, decline codes) must not become rules.
export const HelocGuidelineDocumentRole = {
  Eligibility: 'eligibility',
  Process: 'process',
} as const
export type HelocGuidelineDocumentRole =
  (typeof HelocGuidelineDocumentRole)[keyof typeof HelocGuidelineDocumentRole]

// Unlisted partner documents default to eligibility so a new PDF is read, never silently skipped.
const PROCESS_DOCUMENT_PATTERNS: ReadonlyArray<RegExp> = [
  /^figure_decline_reasons/,
  /^figure_heloc_app_stages/,
  /^figure_heloc_status_tables/,
]

export function documentRoleFromFileName(
  fileName: string
): HelocGuidelineDocumentRole | null {
  if (partnerFromFileName(fileName) === null) return null
  const normalized = fileName.trim().replace(/\s+/g, ' ').toLowerCase()
  return PROCESS_DOCUMENT_PATTERNS.some((pattern) => pattern.test(normalized))
    ? HelocGuidelineDocumentRole.Process
    : HelocGuidelineDocumentRole.Eligibility
}

export const RULE_SET_SCHEMA_VERSION = 1

export const OwnershipType = {
  Individual: 'individual',
  RevocableTrust: 'revocable_trust',
  IrrevocableTrust: 'irrevocable_trust',
  Llc: 'llc',
  Other: 'other',
} as const
export type OwnershipType = (typeof OwnershipType)[keyof typeof OwnershipType]

/**
 * The closed list of inputs the engine knows how to read. Each entry names its source on
 * `ApplicationRevision`, "derived" (computed by the snapshot builder), or "not collected yet"
 * (stays in the union so rules can name it; the engine returns `incomplete`).
 * Money is in dollars. Ratios (`loan.cltv`, `borrower.dti`) are decimals: 0.85 means 85%.
 */
export const SNAPSHOT_FIELDS = [
  // ApplicationRevision.state (property_state is never populated on HomeEquity revisions)
  'property.state',
  // ApplicationRevision.property_type; PropertyType values. NotSpecified is not a value a
  // rule may name; the snapshot builder maps it to null (unknown).
  'property.type',
  // ApplicationRevision.property_use; PropertyUse values, same NotSpecified handling
  'property.occupancy',
  // AVMPropertyInfo.estimated_value (latest live row); the borrower's property_value is not used
  'property.value',
  // ApplicationRevision.remaining_balance
  'property.existing_first_lien_balance',
  // derived: outstanding subject-property mortgage/HELOC liabilities behind the first lien
  'property.existing_second_lien_balance',
  // ApplicationRevision.is_current_property_listed
  'property.listed_for_sale',
  // AVMPropertyInfo.acres (CoreLogic site.acres); unknown when CoreLogic has no lot size
  'property.acreage',
  // derived from the CoreLogic owner record on AVMPropertyInfo (corporate_owner flag, owner
  // names); OwnershipType values; unknown when CoreLogic has no owner names
  'property.ownership_type',
  // derived: days since AVMPropertyInfo.sale_date (the county-recorded transfer)
  'property.seasoning_days',
  // ApplicationRevision.credit_score
  'borrower.credit_score',
  // ApplicationRevision.debt_to_income, stored as a percent (35) and empty on HomeEquity
  // revisions; the snapshot builder divides by 100 so rules compare decimals
  'borrower.dti',
  // derived: retained REOs with an open mortgage/HELOC plus the subject, as sent to DU
  'borrower.financed_property_count',
  // ApplicationRevision.cash_out (HELOC loan_amount mirrors it)
  'loan.requested_amount',
  // derived: (first lien + junior liens + requested amount) / property value; decimal ratio
  'loan.cltv',
  // derived: CLTV numerator plus the undrawn credit on open subject HELOCs, over property value
  'loan.hcltv',
  // derived: 1 + outstanding subject-property liens (first lien plus junior liens)
  'loan.lien_position',
] as const

export type SnapshotField = (typeof SNAPSHOT_FIELDS)[number]

export const snapshotFieldSchema = z.enum(SNAPSHOT_FIELDS)

type FieldType =
  | { kind: 'number'; integer?: true; min?: number; max?: number }
  | { kind: 'ratio' }
  | { kind: 'boolean' }
  | { kind: 'enum'; values: readonly string[] }

const RATIO_MAX = 2

const NOT_SPECIFIED = 'NotSpecified'

const SNAPSHOT_FIELD_TYPES: Record<SnapshotField, FieldType> = {
  'property.state': { kind: 'enum', values: Object.values(StateAbbreviation) },
  'property.type': {
    kind: 'enum',
    values: Object.values(PropertyType).filter(
      (value) => value !== NOT_SPECIFIED
    ),
  },
  'property.occupancy': {
    kind: 'enum',
    values: Object.values(PropertyUse).filter(
      (value) => value !== NOT_SPECIFIED
    ),
  },
  'property.value': { kind: 'number' },
  'property.existing_first_lien_balance': { kind: 'number' },
  'property.existing_second_lien_balance': { kind: 'number' },
  'property.listed_for_sale': { kind: 'boolean' },
  'property.acreage': { kind: 'number' },
  'property.ownership_type': {
    kind: 'enum',
    values: Object.values(OwnershipType),
  },
  'property.seasoning_days': { kind: 'number', integer: true },
  'borrower.credit_score': {
    kind: 'number',
    integer: true,
    min: 300,
    max: 850,
  },
  'borrower.dti': { kind: 'ratio' },
  'borrower.financed_property_count': { kind: 'number', integer: true },
  'loan.requested_amount': { kind: 'number' },
  'loan.cltv': { kind: 'ratio' },
  'loan.hcltv': { kind: 'ratio' },
  'loan.lien_position': { kind: 'number', integer: true, min: 1 },
}

/** Allowed values for the enum-typed snapshot fields; rules must use these exact strings. */
export const SNAPSHOT_FIELD_VALUES: Partial<
  Record<SnapshotField, readonly string[]>
> = Object.fromEntries(
  Object.entries(SNAPSHOT_FIELD_TYPES).flatMap(([field, type]) =>
    type.kind === 'enum' ? [[field, type.values]] : []
  )
)

function fieldValueSchema(type: FieldType): z.ZodTypeAny {
  switch (type.kind) {
    case 'number': {
      let schema = z
        .number()
        .finite()
        .gte(type.min ?? 0)
      if (type.integer) schema = schema.int()
      if (type.max !== undefined) schema = schema.lte(type.max)
      return schema
    }
    case 'ratio':
      return z.number().gte(0).lte(RATIO_MAX)
    case 'boolean':
      return z.boolean()
    case 'enum':
      return z.enum(type.values as [string, ...string[]])
  }
}

/**
 * Snapshot values typed per field, for what-if overrides; every field is optional and `null`
 * clears a stored value to unknown.
 */
export const snapshotOverridesSchema = z
  .object(
    Object.fromEntries(
      SNAPSHOT_FIELDS.map((field) => [
        field,
        fieldValueSchema(SNAPSHOT_FIELD_TYPES[field]).nullable().optional(),
      ])
    ) as { [K in SnapshotField]: z.ZodOptional<z.ZodNullable<z.ZodTypeAny>> }
  )
  .strict()

const scalarSchema = z.union([z.number(), z.string(), z.boolean()])
const listItemSchema = z.union([z.number(), z.string()])

// Every object is strict: an unknown key (a misspelled `applies_when`, say) fails validation
// instead of being stripped and silently changing what the rule means.
const conditionShapeSchema = z.discriminatedUnion('op', [
  z
    .object({
      field: snapshotFieldSchema,
      op: z.enum(['eq', 'neq']),
      value: scalarSchema,
    })
    .strict(),
  z
    .object({
      field: snapshotFieldSchema,
      op: z.enum(['lt', 'lte', 'gt', 'gte']),
      value: z.number(),
    })
    .strict(),
  z
    .object({
      field: snapshotFieldSchema,
      op: z.enum(['in', 'not_in']),
      value: z.array(listItemSchema).min(1),
    })
    .strict(),
  z
    .object({
      field: snapshotFieldSchema,
      op: z.literal('between'),
      value: z
        .tuple([z.number(), z.number()])
        .refine(([min, max]) => min <= max, 'between needs [min, max]'),
    })
    .strict(),
])

function valueMatchesField(
  type: FieldType,
  value: number | string | boolean
): boolean {
  switch (type.kind) {
    case 'number':
      return typeof value === 'number'
    case 'ratio':
      return typeof value === 'number' && value > 0 && value <= RATIO_MAX
    case 'boolean':
      return typeof value === 'boolean'
    case 'enum':
      return typeof value === 'string' && type.values.includes(value)
  }
}

/**
 * Operator semantics on a snapshot value `actual`: `between` is inclusive on both ends;
 * `in`/`not_in` test membership; the rest are the obvious comparisons. A null or missing
 * `actual` is never compared. In `check` it makes the rule `incomplete`; in `applies_when` it
 * makes the rule `skipped` with the field reported in `missing_fields`, and the engine must
 * treat that as an incomplete evaluation, never as "the rule does not apply". The engine's
 * optional fields (DTI) are the one exception: unknown there means the condition is not checked.
 */
export const ruleConditionSchema = conditionShapeSchema.superRefine(
  (condition, ctx) => {
    const type = SNAPSHOT_FIELD_TYPES[condition.field]
    const raw: unknown = condition.value
    const values = (Array.isArray(raw) ? raw : [raw]) as (
      | number
      | string
      | boolean
    )[]
    const ordered = ['lt', 'lte', 'gt', 'gte', 'between'].includes(condition.op)
    if (ordered && type.kind !== 'number' && type.kind !== 'ratio') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['op'],
        message: `${condition.field} is not numeric; use eq, neq, in or not_in`,
      })
      return
    }
    if (type.kind === 'boolean' && !['eq', 'neq'].includes(condition.op)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['op'],
        message: `${condition.field} is boolean; use eq or neq`,
      })
      return
    }
    const invalid = values.filter((value) => !valueMatchesField(type, value))
    if (invalid.length === 0) return
    const expected =
      type.kind === 'enum'
        ? `one of ${type.values.join(', ')}`
        : type.kind === 'ratio'
          ? `a decimal ratio in (0, ${RATIO_MAX}] (write 0.85 for 85%)`
          : `a ${type.kind}`
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['value'],
      message: `${condition.field} expects ${expected}; got ${JSON.stringify(invalid)}`,
    })
  }
)

export type RuleCondition = z.infer<typeof ruleConditionSchema>
export type RuleOperator = RuleCondition['op']

export const ruleSeveritySchema = z.enum(['blocking', 'warning'])
export type RuleSeverity = z.infer<typeof ruleSeveritySchema>

const ruleSourceSchema = z
  .object({
    file_name: z.string().trim().min(1),
    page: z.number().int().positive(),
    quote: z.string().optional(),
  })
  .strict()
export type RuleSource = z.infer<typeof ruleSourceSchema>

const ruleBaseShape = {
  id: z.string().regex(/^[a-z0-9]+(_[a-z0-9]+)*$/, 'snake_case slug'),
  description: z.string().trim().min(1),
  severity: ruleSeveritySchema,
  applies_when: z.array(ruleConditionSchema).optional(),
  source: ruleSourceSchema,
}

const checkRuleSchema = z
  .object({
    ...ruleBaseShape,
    kind: z.literal('check'),
    check: ruleConditionSchema,
  })
  .strict()

const matrixRowSchema = z
  .object({
    label: z.string().optional(),
    when: z.array(ruleConditionSchema).min(1),
    limits: z.array(ruleConditionSchema).min(1),
  })
  .strict()

/**
 * A tier table whose rows overlap (Figure's eligibility matrices, NFTYDoor's credit matrix).
 * Passes when at least one row has every `when` and every `limits` condition true. Fails
 * when every row is conclusively ruled out. Otherwise `incomplete`.
 */
const matrixRuleSchema = z
  .object({
    ...ruleBaseShape,
    kind: z.literal('matrix'),
    rows: z.array(matrixRowSchema).min(1),
  })
  .strict()

export const ruleSchema = z.discriminatedUnion('kind', [
  checkRuleSchema,
  matrixRuleSchema,
])

export type Rule = z.infer<typeof ruleSchema>
export type CheckRule = z.infer<typeof checkRuleSchema>
export type MatrixRule = z.infer<typeof matrixRuleSchema>
export type MatrixRow = z.infer<typeof matrixRowSchema>

export const ambiguitySeveritySchema = z.enum(['blocking', 'material'])
export type AmbiguitySeverity = z.infer<typeof ambiguitySeveritySchema>

/**
 * A conflict or gap the generator found in the documents. The rule carries the conservative
 * reading; the reviewer approving the draft resolves the doubt here. The engine ignores it.
 * `resolved_by` / `resolved_at` are stamped by the review page, never by the generator.
 */
export const ambiguitySchema = z
  .object({
    topic: z.string().trim().min(1),
    issue: z.string().trim().min(1),
    sources: z.array(ruleSourceSchema).min(1),
    severity: ambiguitySeveritySchema,
    resolution: z.string().trim().min(1).optional(),
    resolved_by: z.string().uuid().optional(),
    resolved_at: z.string().datetime().optional(),
  })
  .strict()
export type Ambiguity = z.infer<typeof ambiguitySchema>

/** The single validator for generator output, the save tool, and the engine. */
export const ruleSetSchema = z
  .object({
    schema_version: z.literal(RULE_SET_SCHEMA_VERSION),
    partner: helocPartnerSchema,
    summary: z.string().trim().min(1),
    // lower = preferred when several partners are eligible
    priority: z.number().int().nonnegative(),
    // product or channel choices baked into the rules, e.g. "retail channel", "fixed-rate HELOC"
    assumptions: z.array(z.string()).optional(),
    rules: z.array(ruleSchema).min(1),
    ambiguities: z.array(ambiguitySchema).optional(),
    derived_fields_note: z.string().optional(),
  })
  .strict()
  .superRefine((ruleSet, ctx) => {
    const seen = new Set<string>()
    ruleSet.rules.forEach((rule, index) => {
      if (seen.has(rule.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rules', index, 'id'],
          message: `duplicate rule id "${rule.id}"`,
        })
      }
      seen.add(rule.id)
    })
  })

export type RuleSet = z.infer<typeof ruleSetSchema>

export type SnapshotValue = number | string | boolean

/** One value per `SnapshotField`; `undefined` or `null` means unknown. */
export type ApplicationSnapshot = {
  [K in SnapshotField]?: SnapshotValue | null
}

export type RuleResultOutcome = 'pass' | 'fail' | 'incomplete' | 'skipped'

/**
 * `field` / `actual` / `expected` are the condition that decided the outcome. For a failed
 * matrix that is the loosest limit broken by the rows the file fit by `when` (or, when unknown
 * `when` fields left no row fully matched, by the rows it could still fit), else the first
 * ruled-out `when`.
 * `skipped` with a `missing_field` means an `applies_when` field was unknown: incomplete.
 * `skipped` with `optional_skipped` means the rule decided only on an optional field (the
 * engine's `OPTIONAL_FIELDS`, e.g. DTI) that was unknown: not checked, not incomplete.
 */
export interface RuleResult {
  rule_id: string
  description: string
  severity: RuleSeverity
  outcome: RuleResultOutcome
  field: SnapshotField
  actual: unknown
  /** The condition that decided the outcome. Stored runs keep it on `fail` only. */
  expected?: RuleCondition
  missing_field?: SnapshotField
  /** Matrix only: every unknown field across the rows still in play (superset of `missing_field`). */
  missing_fields?: SnapshotField[]
  /** Matrix only: the rows behind `expected`, with the limit each one broke. */
  detail?: string
  /**
   * Matrix fail only. `matched`: rows the file fit by `when`. `possible`: rows whose `when`
   * had unknown fields but no known false one. Both 0 means every row was ruled out by `when`.
   */
  matrix_rows?: { matched: number; possible: number }
  /** Every condition the rule has left decides on an optional field that is unknown, so it was not checked. */
  optional_skipped?: true
}

export type PartnerEvaluationOutcome = 'eligible' | 'ineligible' | 'incomplete'

export interface PartnerEvaluation {
  partner: HelocPartner
  rule_set_version: number | null
  outcome: PartnerEvaluationOutcome
  blocking_failures: RuleResult[]
  warnings: RuleResult[]
  missing_fields: SnapshotField[]
  /** Reserved for cross-partner ranking; always null in v1. */
  rank: number | null
  rules: RuleResult[]
}

/**
 * One rule as persisted in `heloc_guideline_evaluations.result`: only what the rule set
 * (reachable through `rule_set_id`) cannot supply. `description` and `severity` are rejoined
 * from the rule on read; `expected` is kept on failures, where the UI and Patrick show it.
 */
export type StoredRuleResult = Omit<RuleResult, 'description' | 'severity'>

/** `PartnerEvaluation` as persisted: `blocking_failures` and `warnings` are rebuilt from `rules`. */
export type StoredPartnerEvaluation = Omit<
  PartnerEvaluation,
  'blocking_failures' | 'warnings' | 'rules'
> & { rules: StoredRuleResult[] }

export interface HelocEligibilityRun {
  runId: string | null
  applicationId: number
  revisionId: number
  snapshot: ApplicationSnapshot
  evaluations: PartnerEvaluation[]
  partnersWithoutRules: HelocPartner[]
  triggeredBy: string
  createdAt: string
}
